import { RIDE_DETAILS_API_BASE } from "@/config/apiConfig";
import { publicAuthorizedFetch } from "@/services/publicAuthorizedFetch";
import { getSystemTimezoneAbbreviation } from "@/utils/timezone";
import { formatApiDate, formatApiTime } from "@/utils/dateTimeFormat";
import { resolveApiErrorMessage } from "@/utils/apiErrorMessage";
import type {
  BookingApiGuest,
  BookingApiStop,
  BookingApiVehicle,
  BookingDetails,
  BookingDetailsApiResponse,
  BookingDetailsContext,
  BookingGuest,
} from "@/types/bookingDetails";
import type { RideDetailsLocation } from "@/types/rideDetails";
import { getBookingPin } from "@/utils/bookingPin";

export interface FetchBookingDetailsParams {
  bookingId: string;
  /** Defaults to "guest" (the person who booked the seat). */
  context?: BookingDetailsContext;
}

/**
 * Fetches booking details for the given booking ID and maps the raw API payload into the
 * UI model used by the booking-info screen.
 *
 * - `bookingId` and the system timezone abbreviation (e.g. "IST") are sent as query params.
 * - `context` and `bookingId` are sent in the request body.
 * - If `bookingNumber` is missing, the API `message` is shown as an error.
 */
export async function fetchBookingDetails({
  bookingId,
  context = "guest",
}: FetchBookingDetailsParams): Promise<BookingDetails> {
  const trimmedBookingId = bookingId.trim();
  if (!trimmedBookingId) {
    throw new Error("Booking ID is required.");
  }

  const query = new URLSearchParams({
    bookingId: trimmedBookingId,
    timezone: getSystemTimezoneAbbreviation(),
  });

  const res = await publicAuthorizedFetch(
    `${RIDE_DETAILS_API_BASE}/ride-details?${query.toString()}`,
    {
      method: "POST",
      headers: {
        accept: "*/*",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ context, bookingId: trimmedBookingId }),
    }
  );

  const data = (await res.json().catch(() => ({}))) as BookingDetailsApiResponse;

  if (res.status === 404) {
    throw new Error(
      resolveApiErrorMessage(
        data,
        "Booking not found. Please check the Booking ID and try again."
      )
    );
  }

  if (!res.ok) {
    throw new Error(
      resolveApiErrorMessage(
        data,
        `Unable to fetch booking details (status ${res.status}).`
      )
    );
  }

  if (!data.bookingNumber?.trim()) {
    throw new Error(
      resolveApiErrorMessage(
        data,
        "Booking not found. Please check the Booking ID and try again."
      )
    );
  }

  return mapBookingDetails(data);
}

function mapBookingDetails(api: BookingDetailsApiResponse): BookingDetails {
  const pickup = mapStop(api.pickup, "Pickup");
  const drop = mapStop(api.dropOff, "Drop");

  const bookingId = api.bookingNumber!.trim();
  const guests = mapGuests(api.guests);

  return {
    bookingId,
    rideId: api.rideNumber ?? "",
    guestName: guests[0]?.name ?? api.guestName ?? "there",
    boardingPin: getBookingPin(bookingId),
    date: formatApiDate(api.pickup?.dt),
    time: formatApiTime(api.pickup?.tm),
    seatsBooked: api.seatsBooked ?? (guests.length > 0 ? guests.length : 1),
    guests,
    route: { pickup, drop },
    host: {
      name: api.hostName ?? "Your host",
    },
    vehicle: mapVehicle(api.vehicle),
    pickupInstructions:
      api.pickupInstructions?.trim() || buildPickupInstructions(api.vehicle),
  };
}

function mapGuests(guests: BookingApiGuest[] | undefined): BookingGuest[] {
  if (!guests?.length) return [];

  return guests.map((guest) => ({
    name: guest.name?.trim() || "Guest",
    seatNumber: guest.seatNumber?.trim() || "—",
    gender: guest.gender?.trim() || "",
  }));
}

function mapStop(
  stop: BookingApiStop | undefined,
  defaultLabel: string
): RideDetailsLocation {
  return {
    label: defaultLabel,
    locality: stop?.sc?.trim() || undefined,
    address: stop?.sl || stop?.sc || "—",
    lat: stop?.lat,
    lng: stop?.lng,
  };
}

function mapVehicle(vehicle: BookingApiVehicle | undefined) {
  const name = [vehicle?.mk, vehicle?.mdl].filter(Boolean).join(" ") || "Vehicle";
  return {
    name,
    make: vehicle?.mk,
    model: vehicle?.mdl,
    registration: vehicle?.reg,
    color: vehicle?.clr,
  };
}

function buildPickupInstructions(
  vehicle: BookingApiVehicle | undefined
): string | undefined {
  if (!vehicle) return undefined;
  const descriptor = [vehicle.clr, vehicle.mk, vehicle.mdl]
    .filter(Boolean)
    .join(" ");
  if (!descriptor && !vehicle.reg) return undefined;

  const parts: string[] = [];
  if (descriptor) parts.push(`Look for the ${descriptor}`);
  if (vehicle.reg) parts.push(`(${vehicle.reg})`);
  return `${parts.join(" ")}.`;
}
